import React, { useEffect, useMemo, useState } from 'react';
import { WorldSection } from './WorldPageHeader';
import { CopyTextButton } from './CopyTextButton';
import { auditInvasions, invasionChecks } from '../../utils/invasionValidation';
import { fetchCsv } from '../../utils/csv';
import type { Row } from '../../utils/csv';
import { packageUrl } from '../../data/world/package';
import type { WorldData } from '../../types/world';
const BAG_SECTIONS_URL = packageUrl('19_bag_sections.csv');
const RAW_BAG_ROWS_URL = packageUrl('26_raw_bag_rows.csv');
const verdictText = {
  authorized: 'Autorizado',
  unlisted: 'En revisión: sin lista vigente o pendiente de aprobación',
  conflict: 'Conflicto entre fuentes'
} as const;
const verdictTone = {
  authorized: 'text-emerald-200',
  unlisted: 'text-amber-200',
  conflict: 'text-red-200'
} as const;
type BagState = {
  status: 'loading' | 'ok' | 'error';
  sections: Row[];
  raw: Row[];
  error?: string;
};

/** Panel técnico: contraste entre rutas de invasión exportadas y la información aprobada. */
export function InvasionDropCheck({
  w
}: {
  w: WorldData;
}) {
  const audit = useMemo(() => auditInvasions(w), [w]);
  const [bag, setBag] = useState<BagState>({
    status: 'loading',
    sections: [],
    raw: []
  });
  useEffect(() => {
    let alive = true;
    Promise.all([fetchCsv(BAG_SECTIONS_URL), fetchCsv(RAW_BAG_ROWS_URL)]).then(([s, r]) => alive && setBag({
      status: 'ok',
      sections: s.rows,
      raw: r.rows,
      error: [...s.errors, ...r.errors].slice(0, 5).join(' · ') || undefined
    })).catch((e: Error) => alive && setBag({
      status: 'error',
      sections: [],
      raw: [],
      error: e.message
    }));
    return () => {
      alive = false;
    };
  }, []);
  const dragonHelm = useMemo(() => {
    const routes = w.routes.filter(r => r.itemId === '7,1');
    return routes.map(r => ({
      r,
      checks: invasionChecks(w, r)
    }));
  }, [w]);
  const bag2Sections = bag.sections.filter(s => s.bag_id === '2');
  const bag2Raw = bag.raw.filter(s => s.bag_id === '2');
  const text = useMemo(() => {
    const lines = ['CONTRASTE DE INVASIONES — rutas exportadas frente a información aprobada', ''];
    audit.forEach(a => {
      lines.push(`## ${a.policy.name} (${a.policy.eventIds.join(', ')}${a.policy.bagIds.length ? ` · bag ${a.policy.bagIds.join(', ')}` : ''}${a.policy.monsterIds.length ? ` · monster ${a.policy.monsterIds.join(', ')}` : ''})`);
      lines.push(`Aprobado: ${a.policy.approved}`);
      lines.push(`Rutas: ${a.routes} · ítems distintos: ${a.items} · autorizados ${a.authorized} · sin lista vigente ${a.unlisted} · conflicto ${a.conflict}`);
      if (a.policy.missing) lines.push(`Falta: ${a.policy.missing}`);
      a.rows.filter(x => x.verdict !== 'authorized').forEach(x => lines.push(`- [${verdictText[x.verdict]}] ${x.name} (${x.itemId}) ${x.quality ?? ''} · ${x.routeId} · ${x.reason}`));
      lines.push('');
    });
    lines.push('## Dragon Helm (7,1)');
    dragonHelm.forEach(({
      r,
      checks
    }) => lines.push(`- ${r.id} · ${r.kind} · ${r.quality ?? '—'} · bag ${r.bagId ?? '—'} → ${checks.length ? checks.map(c => `${c.policy.name}: ${verdictText[c.verdict]}`).join('; ') : 'no es ruta de invasión (se mantiene)'}`));
    lines.push('', `## bag 2 en 21_bag_rewards.csv: ${w.bagRewards.filter(r => r.bagId === '2').length} filas · ${new Set(w.bagRewards.filter(r => r.bagId === '2').map(r => r.itemId)).size} ítems · 7,1 incluido: ${w.bagRewards.some(r => r.bagId === '2' && r.itemId === '7,1') ? 'sí' : 'no'}`);
    lines.push(`## bag 2 en 19_bag_sections.csv:`);
    lines.push(`   detalle: ${bag.status === 'ok' ? `${bag2Sections.length} secciones · used_by_runtime: ${bag2Sections.map(s => `${s.section_id}=${s.used_by_runtime || '—'}`).join(', ')}` : bag.status}`);
    lines.push(`## bag 2 en raw_bag_rows.csv: ${bag.status === 'ok' ? `${bag2Raw.length} líneas` : bag.status}`);
    return lines.join('\n');
  }, [w, audit, dragonHelm, bag, bag2Sections, bag2Raw]);
  return <WorldSection id="invasiones-drops" title="Drops de invasión: exportación frente a lo aprobado" data-id="element-1361" data-mp="1tingqkmbfmro">
      <p className="mb-4 max-w-3xl text-sm text-gray-400" data-id="element-1362" data-mp="wr9ywirpertb">
        Una ruta de invasión solo se muestra a los jugadores si el ítem está autorizado de forma explícita. Los casos “Sin lista vigente” y “Conflicto entre fuentes” se retienen; las demás formas de obtener esos ítems se mantienen. Los archivos originales no se modifican.
      </p>
      <div className="mb-5" data-id="element-1363" data-mp="1aj6n4bq94hkf">
        <CopyTextButton text={text} label="Copiar contraste de invasiones" data-id="element-1364" data-mp="1fgym1ppbd3y4" />
      </div>

      <div className="overflow-x-auto rounded-2xl border border-white/10" data-id="element-1365" data-mp="1fjb7icua5wfx">
        <table className="w-full min-w-[640px] text-left text-sm" data-id="element-1366" data-mp="1ke8cn8ofvlnj">
          <thead className="bg-white/[0.04] text-gray-400" data-id="element-1367" data-mp="1oipv94j2bswq">
            <tr data-id="element-1368" data-mp="1tgr9nc5jn41v">
              <th className="px-4 py-3 font-semibold" data-id="element-1369" data-mp="1th9f8ze3iumg">Invasión</th>
              <th className="px-4 py-3 font-semibold" data-id="element-1370" data-mp="1tedk1itmuk1p">Rutas</th>
              <th className="px-4 py-3 font-semibold" data-id="element-1371" data-mp="3qab7l5ydwdwl">Ítems</th>
              <th className="px-4 py-3 font-semibold text-emerald-200" data-id="element-1372" data-mp="3v7kv2g27qfhs">Autorizados</th>
              <th className="px-4 py-3 font-semibold text-amber-200" data-id="element-1373" data-mp="3nobmuq5g0mub">Sin lista</th>
              <th className="px-4 py-3 font-semibold text-red-200" data-id="element-1374" data-mp="3nqeok7fyk4ev">Conflicto</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-white/5" data-id="element-1375" data-mp="3dyinqomhm06y">
            {audit.map(a => <tr key={a.policy.guideEntryId} data-id="element-1376" data-mp="38zp8orrjy14o">
                <td className="px-4 py-3 align-top" data-id="element-1377" data-mp="390qo18flp9s7">
                  <span className="font-semibold text-white" data-id="element-1378" data-mp="ai1qylnqdfo9">{a.policy.name}</span>
                  {a.policy.missing && <span className="mt-1 block text-xs text-gray-500" data-id="element-1379" data-mp="5iyvnsxjynth">Falta: {a.policy.missing}</span>}
                </td>
                <td className="px-4 py-3 font-mono text-white" data-id="element-1380" data-mp="j489zczkgd6">{a.routes}</td>
                <td className="px-4 py-3 font-mono text-white" data-id="element-1381" data-mp="3snybrwumvriy">{a.items}</td>
                <td className="px-4 py-3 font-mono text-emerald-200" data-id="element-1382" data-mp="3sprbstaqrdhz">{a.authorized}</td>
                <td className="px-4 py-3 font-mono text-amber-200" data-id="element-1383" data-mp="2lbfotvdwfng6">{a.unlisted}</td>
                <td className="px-4 py-3 font-mono text-red-200" data-id="element-1384" data-mp="2h6ya4amec0c9">{a.conflict}</td>
              </tr>)}
          </tbody>
        </table>
      </div>

      <h3 className="mt-6 mb-2 font-semibold text-white" data-id="element-1385" data-mp="2npb7ujpu0ggd">Caso de verificación: Dragon Helm (7,1)</h3>
      <ul className="space-y-1.5 text-sm" data-id="element-1386" data-mp="28s0nekv7o08i">
        {dragonHelm.length === 0 && <li className="text-gray-400" data-id="element-1387" data-mp="28tv5lrf3le8w">Sin rutas para 7,1 en los archivos cargados.</li>}
        {dragonHelm.map(({
        r,
        checks
      }) => <li key={r.id} className="rounded-xl bg-white/[0.03] px-3 py-2 text-gray-300" data-id="element-1388" data-mp="1zyxb4fwaxtfg">
            <span className="font-mono text-white" data-id="element-1389" data-mp="24qff7ctmga4n">{r.id}</span> · {r.kind} · {r.quality ?? '—'}{r.bagId ? ` · bag ${r.bagId}` : ''} →{' '}
            {checks.length ? checks.map(c => <span key={c.policy.guideEntryId} className={verdictTone[c.verdict]} data-id="element-1390" data-mp="1pz6jclz4lsj2">{c.policy.name}: {verdictText[c.verdict]} </span>) : <span className="text-emerald-200" data-id="element-1391" data-mp="1gzt5lh3yyhsj">no es ruta de invasión, se mantiene</span>}
          </li>)}
      </ul>

      <h3 className="mt-6 mb-2 font-semibold text-white" data-id="element-1392" data-mp="1l0v764lcz5xo">Red Dragon · bolsa 2 (paquete 08/10/2026)</h3>
      <ul className="space-y-1 text-sm text-gray-300" data-id="element-1393" data-mp="12efbfjit9kfd">
        <li data-id="element-1394" data-mp="xej9dptoc32g">16_bags: {w.bags.get('2') ? `${w.bags.get('2')!.name ?? 'sin nombre'} · delivery_mode ${w.bags.get('2')!.deliveryMode ?? '—'} · used_by_runtime ${String(w.bags.get('2')!.usedByRuntime ?? '—')}` : 'no figura'}</li>
        <li data-id="element-1395" data-mp="16ey4fgwfwwj5">17_bag_triggers: {(w.idx.bagTriggersByBag.get('2') ?? []).map(t => t.itemId ? `ítem ${t.itemId}` : t.monsterId !== undefined ? `monstruo ${t.monsterId}` : t.id).join(', ') || 'sin activadores'}</li>
        <li data-id="element-1396" data-mp="1gcc78dpfxmsz">21_bag_rewards: {w.bagRewards.filter(r => r.bagId === '2').length} filas · {new Set(w.bagRewards.filter(r => r.bagId === '2').map(r => r.itemId)).size} ítems distintos</li>
        <li data-id="element-1397" data-mp="1q49mpwnhf0xi">34_event_bags: {(w.idx.eventBagsByBag.get('2') ?? []).map(e => `${e.eventId}${e.monsterId !== undefined ? ` · monstruo ${e.monsterId}` : ''}`).join(', ') || 'sin evento'}</li>
        <li data-id="element-1398" data-mp="1z4o9z1k0aq3n">
          19_bag_sections / 26_raw_bag_rows:{' '}
          {bag.status === 'loading' ? 'cargando…' : bag.status === 'error' ? `error de carga: ${bag.error}` : `${bag2Sections.length} secciones y ${bag2Raw.length} líneas de configuración.`}
        </li>
      </ul>
      <p className="mt-2 text-xs text-gray-500" data-id="element-1399" data-mp="3fh0p1mk3eeoy">
        La bolsa 2 es una lista interna de resultados posibles al derrotar al jefe, no una caja que abre el jugador. Sus ítems siguen en revisión para los jugadores hasta que la administración confirme la lista vigente.
      </p>
    </WorldSection>;
}